import { useEffect, useMemo, useState } from "react";
import { api, INR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import KPICard from "@/components/KPICard";
import { ReliabilityBadge } from "@/components/Badges";
import {
  BarChart,
  Bar,
  Line,
  ComposedChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { InlineSpinner, Spinner } from "@/components/Spinner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Stable references — extracted so Recharts doesn't see new object identities on each render.
const CHART_TOOLTIP_STYLE = {
  background: "#ffffff",
  border: "1px solid #e2eae7",
  borderRadius: 6,
  color: "#0a474c",
  fontSize: 12,
  boxShadow: "0 8px 24px rgba(10, 71, 76, 0.08)",
};
const CHART_TOOLTIP_LABEL_STYLE = { color: "#0a474c", fontWeight: 600, marginBottom: 4 };
const CHART_TOOLTIP_ITEM_STYLE = { color: "#0a474c" };
const CHART_TOOLTIP_CURSOR = { fill: "rgba(10, 71, 76, 0.06)" };
const CHART_XAXIS_LINE = { stroke: "#e5e4e1" };
const CHART_DOT = { r: 3, fill: "#03cf65" };
const tooltipFormatter = (v, n) => (n === "Generated" ? v : INR(v));
const yAxisTickFormatter = (v) => {
  if (v >= 1e7) return `${(v / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `${(v / 1e5).toFixed(1)}L`;
  return v;
};

function AgingBuckets({ aging, overdue_amount, onSelect }) {
  const total = Math.max(0, Number(overdue_amount) || 0);
  const rows = [
    { key: "0_30", label: "0 – 30 days", tone: "pending", data: aging["0_30"] },
    { key: "31_60", label: "31 – 60 days", tone: "warn", data: aging["31_60"] },
    { key: "60_plus", label: "60+ days", tone: "overdue", data: aging["60_plus"] },
  ];
  const toneColor = {
    pending: "text-[color:var(--pending)]",
    warn: "text-[color:var(--overdue)]",
    overdue: "text-[color:var(--overdue)]",
  };
  const toneBar = {
    pending: "bg-[color:var(--pending)]",
    warn: "bg-[color:var(--overdue)]",
    overdue: "bg-[color:var(--overdue)]",
  };
  return (
    <div className="border border-line bg-card" data-testid="aging-buckets">
      <div className="px-5 py-3 border-b border-line flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="eyebrow">Aging analysis</div>
          <div className="font-display font-semibold text-lg">Overdue by days late</div>
        </div>
        <div className="text-xs text-ink-2 tabular">
          Total overdue{" "}
          <span className="font-semibold text-[color:var(--overdue)]">{INR(total)}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[color:var(--line)]">
        {rows.map((r) => {
          const amt = Number(r.data?.amount || 0);
          const cnt = Number(r.data?.count || 0);
          const pct = total > 0 ? Math.round((amt / total) * 100) : 0;
          const clickable = onSelect && cnt > 0;
          return (
            <button
              key={r.key}
              type="button"
              onClick={clickable ? () => onSelect(r.key, r.label) : undefined}
              disabled={!clickable}
              className={`bg-card p-4 sm:p-5 flex flex-col gap-2 text-left w-full transition-colors ${
                clickable ? "hover:bg-[color:var(--bone)] cursor-pointer" : "cursor-default"
              }`}
              data-testid={`aging-${r.key}`}
            >
              <div className="flex items-baseline justify-between">
                <div className="eyebrow">{r.label}</div>
                <div className="text-[11px] text-ink-2 tabular">{cnt} inv</div>
              </div>
              <div className={`tabular font-semibold text-xl sm:text-2xl truncate ${toneColor[r.tone]}`}>
                {INR(amt)}
              </div>
              <div className="w-full h-1.5 bg-[color:var(--bone)] overflow-hidden rounded-sm">
                <div
                  className={`h-full ${toneBar[r.tone]}`}
                  style={{ width: `${pct}%` }}
                  data-testid={`aging-${r.key}-bar`}
                />
              </div>
              <div className="text-[11px] text-ink-2 tabular flex items-center justify-between">
                <span>{pct}% of overdue</span>
                {clickable && <span className="text-[color:var(--aisensy-dark)] font-semibold">View entities →</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AgingDrillDialog({ open, onOpenChange, bucketKey, bucketLabel, unit }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open || !bucketKey) return;
    setRows(null);
    setError("");
    api
      .get(`/dashboard/aging-entities`, { params: { bucket: bucketKey, unit } })
      .then((r) => setRows(r.data.entities || []))
      .catch((e) => setError(formatApiError(e)));
  }, [open, bucketKey, unit]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-sm max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="aging-drill-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display">
            Entities overdue · {bucketLabel}
          </DialogTitle>
        </DialogHeader>
        {error ? (
          <div className="text-sm text-[color:var(--overdue)]">{error}</div>
        ) : rows === null ? (
          <div className="py-6 flex justify-center"><Spinner /></div>
        ) : rows.length === 0 ? (
          <div className="py-6 text-center text-ink-2 text-sm">
            No entities in this bucket.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] min-w-[560px]">
              <thead>
                <tr className="border-b border-line text-left">
                  <th className="px-3 py-2 eyebrow">Entity</th>
                  <th className="px-3 py-2 eyebrow">Type</th>
                  <th className="px-3 py-2 eyebrow text-right">Invoices</th>
                  <th className="px-3 py-2 eyebrow text-right">Max late</th>
                  <th className="px-3 py-2 eyebrow text-right">Overdue</th>
                </tr>
              </thead>
              <tbody data-testid="aging-drill-table">
                {rows.map((r) => (
                  <tr
                    key={`${r.entity_type}-${r.entity_id}`}
                    className="row-divider"
                    data-testid={`aging-drill-row-${r.entity_id}`}
                  >
                    <td className="px-3 py-2.5 font-semibold">{r.name}</td>
                    <td className="px-3 py-2.5 text-ink-2 capitalize">{r.entity_type}</td>
                    <td className="px-3 py-2.5 text-right tabular">{r.invoice_count}</td>
                    <td className="px-3 py-2.5 text-right tabular text-ink-2">
                      {r.max_days_late}d
                    </td>
                    <td className="px-3 py-2.5 text-right tabular font-semibold text-[color:var(--overdue)]">
                      {INR(r.overdue_amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UnitToggle({ unit, setUnit }) {
  const opts = [
    { key: "both", label: "All" },
    { key: "client", label: "Clients" },
    { key: "partner", label: "Partners" },
  ];
  return (
    <div
      className="inline-flex bg-card border border-line rounded-sm p-0.5"
      role="tablist"
      aria-label="Business unit filter"
      data-testid="dashboard-unit-toggle"
    >
      {opts.map((o) => {
        const active = o.key === unit;
        return (
          <button
            key={o.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => setUnit(o.key)}
            data-testid={`dashboard-unit-${o.key}`}
            className={
              "px-3 py-1.5 text-[13px] rounded-sm transition-colors tabular " +
              (active
                ? "bg-[color:var(--aisensy-dark)] text-white font-semibold"
                : "text-ink-2 hover:text-ink hover:bg-[color:var(--bone)]")
            }
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}


export default function DashboardPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [reliable, setReliable] = useState([]);
  const [drill, setDrill] = useState({ open: false, bucket: null, label: "" });
  // Admin-only scope switch. Non-admins see their pinned unit; the toggle is hidden.
  // Persisted in localStorage so the last-picked scope survives reloads.
  const [unit, setUnit] = useState(() => {
    if (typeof window === "undefined") return "both";
    const saved = window.localStorage.getItem("dashboard_unit");
    return saved === "client" || saved === "partner" || saved === "both" ? saved : "both";
  });

  useEffect(() => {
    try {
      window.localStorage.setItem("dashboard_unit", unit);
    } catch {
      /* localStorage may be unavailable (private mode / quota) — safe to ignore */
    }
  }, [unit]);

  useEffect(() => {
    (async () => {
      try {
        setSummary(null);
        const relParams = {};
        if (unit === "client" || unit === "partner") {
          relParams.entity_type = unit;
        }
        const [s, r] = await Promise.all([
          api.get("/dashboard/summary", { params: { unit } }),
          api.get("/dashboard/top-reliable", { params: relParams }),
        ]);
        setSummary(s.data);
        setReliable(r.data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [unit]);

  if (!summary) return <InlineSpinner />;

  const chartData = summary.monthly.map((b) => ({
    month: b.month.slice(5) + "/" + b.month.slice(2, 4),
    Invoiced: Math.round(b.invoiced),
    Collected: Math.round(b.collected),
    Generated: b.generated,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Overview</div>
          <h1 className="font-display font-semibold text-xl sm:text-2xl tracking-tight mt-1">
            Hello {user?.name?.split(" ")[0] || "there"}, here&apos;s your receivables snapshot.
          </h1>
          <p className="text-sm text-ink-2 mt-1">
            Scope:{" "}
            <span className="font-semibold text-ink">
              {summary.visible_units.map((u) => u.replace(/^./, (c) => c.toUpperCase())).join(" & ")}
            </span>{" "}
            business unit{summary.visible_units.length > 1 ? "s" : ""}.
          </p>
        </div>
        {user?.role === "admin" && (
          <UnitToggle unit={unit} setUnit={setUnit} />
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[color:var(--line)] border border-line">
        <KPICard
          testid="kpi-outstanding"
          label="Total Outstanding"
          value={INR(summary.outstanding)}
          sub={`${summary.unpaid_count} unpaid invoice${summary.unpaid_count === 1 ? "" : "s"}`}
          tone="danger"
        />
        <KPICard
          testid="kpi-collected"
          label="Total Collected"
          value={INR(summary.collected)}
          sub={`${summary.paid_count} paid invoice${summary.paid_count === 1 ? "" : "s"}`}
          tone="success"
        />
        <KPICard
          testid="kpi-overdue"
          label="Overdue"
          value={INR(summary.overdue_amount)}
          sub={`${summary.overdue_count} past due`}
          tone="warn"
        />
        <KPICard
          testid="kpi-this-month"
          label="This Month · Generated"
          value={INR(summary.month_generated_amount)}
          sub={`${summary.month_generated} invoices · Collected ${INR(summary.month_collected)}`}
        />
      </div>

      {/* Aging Buckets */}
      {summary.aging && (
        <AgingBuckets
          aging={summary.aging}
          overdue_amount={summary.overdue_amount}
          onSelect={user?.role === "admin" ? (bucket, label) => setDrill({ open: true, bucket, label }) : null}
        />
      )}
      <AgingDrillDialog
        open={drill.open}
        onOpenChange={(v) => setDrill((d) => ({ ...d, open: v }))}
        bucketKey={drill.bucket}
        bucketLabel={drill.label}
        unit={unit}
      />

      {/* Chart */}
      <div className="border border-line bg-card">
        <div className="px-5 py-3 border-b border-line flex items-center justify-between">
          <div>
            <div className="eyebrow">Trailing 6 months</div>
            <div className="font-display font-semibold text-lg">Invoiced vs Collected</div>
          </div>
          <div className="flex items-center gap-4 text-xs text-ink-2">
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 bg-[color:var(--aisensy-dark)] inline-block"></span> Invoiced
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 bg-[color:var(--aisensy-green)] inline-block"></span>{" "}
              Collected
            </span>
          </div>
        </div>
        <div className="p-4 h-72" data-testid="chart-invoiced-collected">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              <CartesianGrid stroke="#e5e4e1" strokeDasharray="0" vertical={false} />
              <XAxis
                dataKey="month"
                stroke="#6a6e6a"
                fontSize={11}
                tickLine={false}
                axisLine={CHART_XAXIS_LINE}
              />
              <YAxis
                stroke="#6a6e6a"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tickFormatter={yAxisTickFormatter}
              />
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
                labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                itemStyle={CHART_TOOLTIP_ITEM_STYLE}
                cursor={CHART_TOOLTIP_CURSOR}
                formatter={tooltipFormatter}
              />
              <Bar dataKey="Invoiced" fill="#0a474c" barSize={22} />
              <Line
                type="linear"
                dataKey="Collected"
                stroke="#03cf65"
                strokeWidth={2.5}
                dot={CHART_DOT}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Split by business unit, admin only */}
      {user?.role === "admin" && Object.keys(summary.by_unit || {}).length > 0 && (
        <div className="grid md:grid-cols-2 gap-px bg-[color:var(--line)] border border-line">
          {["client", "partner"].map((et) => {
            const u = summary.by_unit[et];
            if (!u) return null;
            return (
              <div key={et} className="bg-card p-5" data-testid={`unit-card-${et}`}>
                <div className="flex items-center justify-between">
                  <div className="eyebrow">
                    {et === "client" ? "Client Business Unit" : "Partner Business Unit"}
                  </div>
                  <span className="text-[10px] uppercase tracking-wider font-semibold bg-[color:var(--bone)] px-1.5 py-0.5 border border-line">
                    {u.count} inv
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
                  <div>
                    <div className="text-xs text-ink-2">Outstanding</div>
                    <div className="tabular font-semibold text-xl text-[color:var(--overdue)] mt-1 truncate">
                      {INR(u.outstanding)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-ink-2">Overdue</div>
                    <div className="tabular font-semibold text-xl text-[color:var(--pending)] mt-1 truncate">
                      {INR(u.overdue || 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-ink-2">Collected</div>
                    <div className="tabular font-semibold text-xl text-forest mt-1 truncate">
                      {INR(u.collected)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reliability leaderboard */}
      <div className="border border-line bg-card">
        <div className="px-5 py-3 border-b border-line">
          <div className="eyebrow">Payment Reliability</div>
          <div className="font-display font-semibold text-lg">
            Who pays on time and who doesn&apos;t
          </div>
        </div>
        <div className="overflow-x-auto">
          {reliable.length === 0 ? (
            <div className="p-8 text-center text-ink-2 text-sm">
              No payment history yet.
            </div>
          ) : (
            <table className="w-full text-[13px] min-w-[640px]">
              <thead>
                <tr className="row-divider text-left">
                  <th className="px-5 py-2 eyebrow">Entity</th>
                  <th className="px-5 py-2 eyebrow">Type</th>
                  <th className="px-5 py-2 eyebrow text-right">Paid / Total</th>
                  <th className="px-5 py-2 eyebrow text-right">Avg Late (days)</th>
                  <th className="px-5 py-2 eyebrow text-right">Outstanding</th>
                  <th className="px-5 py-2 eyebrow text-right">Reliability</th>
                </tr>
              </thead>
              <tbody data-testid="reliability-table">
                {reliable.slice(0, 12).map((r) => (
                  <tr key={`${r.entity_type}-${r.id}`} className="row-divider hover:bg-[color:var(--bone)]">
                    <td className="px-5 py-2.5 font-semibold">{r.name}</td>
                    <td className="px-5 py-2.5 text-ink-2 capitalize">{r.entity_type}</td>
                    <td className="px-5 py-2.5 text-right tabular">
                      {r.paid_invoices} / {r.total_invoices}
                    </td>
                    <td className="px-5 py-2.5 text-right tabular">
                      {r.avg_late_days > 0 ? `${r.avg_late_days}d` : "-"}
                    </td>
                    <td className="px-5 py-2.5 text-right tabular">
                      {INR(r.outstanding)}
                    </td>
                    <td className="px-5 py-2.5 text-right">
                      <ReliabilityBadge score={r.score} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
